/**
 * Keeps the server's writable derived figures — discount value, PO cost, tax amounts — equal to
 * what the page shows. Renders nothing. See `lib/derivedFigures.ts` for which fields and why.
 *
 * Fires when the STORED document (never a pending preview) disagrees with the computed figures,
 * once per distinct set: the write itself changes the document, the refetch brings it back
 * equal, and `derivedFiguresStale` then says nothing — so there is no loop. A failed write is
 * reported by the mutation and retried only when the figures change again.
 */

import { useEffect, useRef } from 'react'
import { derivedFiguresPatch, derivedFiguresStale, type DerivedFigures } from '../../lib/derivedFigures'
import type { Quotation } from '../../lib/types'

export function DerivedFiguresSync({
  quotation,
  enabled,
  write,
}: {
  quotation: Quotation
  /** False while another write is in flight, or while the page is read-only. */
  enabled: boolean
  write: (figures: DerivedFigures) => void
}) {
  const lastWritten = useRef<string | null>(null)
  useEffect(() => {
    if (!enabled) return
    const figures = derivedFiguresPatch(quotation)
    if (!figures || !derivedFiguresStale(quotation, figures)) return
    const signature = JSON.stringify(figures)
    if (signature === lastWritten.current) return
    /*
      Off the mount tick, with cleanup — never `write()` straight from the effect.

      React Query's MutationObserver drops a running mutation the moment the hook's listener is
      torn down (`onUnsubscribe` → `removeObserver`; nothing re-adds it), and StrictMode tears
      every listener down and up once right after mount. A mutation started INSIDE the mount
      effects therefore completed unheard: `isPending` stayed true for ever, `saving` dimmed and
      disabled every panel, and the page read as locked (Akhil, 2026-09-16, measured: PUT 200 at
      3.0s, panel still disabled at 17s). A zero-delay timer lands after the simulated remount,
      and its cleanup cancels the first pass's timer so the write still happens exactly once.
    */
    const timer = setTimeout(() => {
      lastWritten.current = signature
      write(figures)
    }, 0)
    return () => clearTimeout(timer)
  }, [quotation, enabled, write])
  return null
}
